import api from "../../../../lib/api";
import { toast } from "sonner";
import { FileText, Plus } from "lucide-react";

/**
 * AutomationRulesPanel — BYO carrier accounts + rule-based routing engine
 * (Phase 3). Lists connected carriers with credentials + the ordered rule
 * table. Rule and carrier credential editors live as sibling dialogs.
 *
 * Extracted from SellerDropship.jsx in Phase 3 refactor.
 */
export default function AutomationRulesPanel({
  rules, setRules, rulesMeta, ruleDraft, setRuleDraft,
  carrierAccounts, setCarrierAccounts, setCarrierDialog,
  setTemplatesOpen, supportedCarriers,
}) {
  return (
  <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-automation-panel">
    <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
      <div>
        <div className="text-[13px] font-medium">Automation</div>
        <div className="text-[11px] text-foreground/55 mt-0.5">
          Write rules that decide which carrier a Verqo-brokered label uses.
        </div>
      </div>
      <button
        onClick={() => setTemplatesOpen(true)}
        data-testid="automation-templates-open"
        className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
        <FileText className="w-3 h-3"/>Notification templates
      </button>
    </div>
    <div className="p-5 space-y-6">
      {/* BYO carrier accounts — DISCONTINUED (Iter 217).
          The "+ Add carrier" entrypoint is hidden. Existing accounts (if
          any) are shown read-only with a Remove button so sellers can
          clean up historical data. */}
      {(carrierAccounts || []).length > 0 && (
        <div data-testid="fulfillment-legacy-byo">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-[13px] font-medium">Legacy carrier accounts</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">
                Adding new carrier accounts is no longer supported. Existing entries can be removed here.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {carrierAccounts.map(a => (
              <div key={a.id} className="rounded-xl border border-border bg-white p-3" data-testid={`carrier-account-${a.carrier}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{a.nickname || a.carrier}</div>
                    <div className="text-[11px] text-foreground/55 mono">
                      {a.carrier?.toUpperCase()} · {a.account_masked || "no account #"}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Remove ${a.nickname || a.carrier}?`)) return;
                      try {
                        await api.delete(`/dropship/carrier-accounts/${a.id}`);
                        setCarrierAccounts(list => list.filter(x => x.id !== a.id));
                        toast.success("Carrier removed");
                      } catch { toast.error("Could not remove"); }
                    }}
                    data-testid={`remove-carrier-${a.carrier}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 text-rose-700 hover:bg-rose-50">
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Automation rules */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[13px] font-medium">Routing rules</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              First matching rule wins. Fall-through goes to your preferred carrier setting.
            </div>
          </div>
          <button
            onClick={() => setRuleDraft({
              name: "New rule",
              priority: (rules.length + 1) * 10,
              active: true,
              condition: { field: "ship_to.state", op: "eq", value: "" },
              action: { carrier: "", service_token: "", add_insurance: false, min_insurance: null },
              actions: [],
            })}
            data-testid="add-rule-button"
            className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
            <Plus className="w-3 h-3"/>Add rule
          </button>
        </div>
        {rules.length === 0 && !ruleDraft ? (
          <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-center text-[12px] text-foreground/55">
            No routing rules yet. Sellers typically add rules like &ldquo;heavy orders → UPS Ground&rdquo; or &ldquo;CA/OR/WA → USPS Priority&rdquo;.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((r, idx) => (
              <div key={r.id} className="rounded-xl border border-border bg-white p-3" data-testid={`rule-${r.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-1 rounded bg-secondary text-foreground/70">P{r.priority}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{r.name}</div>
                    <div className="text-[11px] text-foreground/55 mono truncate">
                      IF {r.condition.field} {rulesMeta.allowed_ops[r.condition.op] || r.condition.op} {Array.isArray(r.condition.value) ? r.condition.value.join(", ") : String(r.condition.value)}
                      {" → "}
                      {r.action.carrier ? `carrier: ${r.action.carrier}` : ""}
                      {r.action.service_token ? ` · service: ${r.action.service_token}` : ""}
                      {r.action.add_insurance ? " · insure" : ""}
                      {(r.actions && r.actions.length) ? ` · +${r.actions.length} chain step${r.actions.length === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {idx > 0 && (
                      <button
                        onClick={async () => {
                          const newOrder = [...rules];
                          [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
                          try {
                            await api.post("/dropship/automation-rules/reorder", { order: newOrder.map(x => x.id) });
                            const { data } = await api.get("/dropship/automation-rules");
                            setRules(data?.rules || []);
                          } catch { toast.error("Could not reorder"); }
                        }}
                        className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2 border border-border hover:bg-secondary" title="Move up">
                        ↑
                      </button>
                    )}
                    <label className="text-[10.5px] mono uppercase tracking-[0.14em] inline-flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={r.active}
                        onChange={async e => {
                          const patch = { ...r, active: e.target.checked };
                          try {
                            await api.put(`/dropship/automation-rules/${r.id}`, patch);
                            setRules(list => list.map(x => x.id === r.id ? { ...x, active: e.target.checked } : x));
                          } catch { toast.error("Could not update"); }
                        }}
                        className="w-3.5 h-3.5 accent-foreground"/>
                      active
                    </label>
                    <button
                      onClick={() => setRuleDraft(r)}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary">
                      Edit
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete rule "${r.name}"?`)) return;
                        try {
                          await api.delete(`/dropship/automation-rules/${r.id}`);
                          setRules(list => list.filter(x => x.id !== r.id));
                          toast.success("Rule removed");
                        } catch { toast.error("Could not remove"); }
                      }}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 text-rose-700 hover:bg-rose-50">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
  );
}
